import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { UserPlus, ShieldCheck, X, Clock, QrCode, Check } from "lucide-react";

const DURATIONS = [
  { label: "1 hour", secs: 3600 },
  { label: "4 hours", secs: 14400 },
  { label: "1 day", secs: 86400 },
];

function fmt(s) {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

export default function GuestPassDemo() {
  const [name, setName] = useState("");
  const [duration, setDuration] = useState(DURATIONS[1]);
  const [pass, setPass] = useState(null); // {name, remaining}
  const [granting, setGranting] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearInterval(timer.current), []);

  const grant = () => {
    const guest = name.trim() || "Guest";
    setGranting(true);
    clearInterval(timer.current);
    setTimeout(() => {
      // fast-forward countdown for demo feel (accelerated)
      const start = Math.min(duration.secs, 3600);
      setPass({ name: guest, remaining: start, total: start, devices: 0 });
      setGranting(false);
      timer.current = setInterval(() => {
        setPass((p) => {
          if (!p) return p;
          const remaining = p.remaining - 60; // demo: 1 tick = 1 minute
          if (remaining <= 0) { clearInterval(timer.current); return null; }
          return { ...p, remaining, devices: Math.min(3, p.devices + (Math.random() > 0.5 ? 1 : 0)) };
        });
      }, 1000);
    }, 1100);
  };

  const revoke = () => { clearInterval(timer.current); setPass(null); setName(""); };

  return (
    <div className="overflow-hidden rounded-3xl border border-white/10 bg-navy-deep" data-testid="poc-guest-pass">
      <div className="grid lg:grid-cols-2">
        {/* Left: control */}
        <div className="border-b border-white/10 p-7 md:p-10 lg:border-b-0 lg:border-r">
          <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-revo-green">Entitlement demo · Guest pass</p>
          <h3 className="mt-2 font-display text-3xl font-extrabold text-white">The cloud carries the permission.</h3>
          <p className="mt-3 text-sm leading-relaxed text-revo-ice">
            Transmitters power only serials you authorize. Grant a visitor a time-boxed guest pass and their phone charges on your surfaces — until the pass expires or you revoke it. No one else gets a single watt.
          </p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-revo-ice">Guest name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Sam from 4B"
                data-testid="guest-name-input"
                className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-revo-ice/40 focus:border-revo-green focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-revo-ice">Access window</label>
              <div className="flex gap-2" data-testid="guest-durations">
                {DURATIONS.map((d) => (
                  <button
                    key={d.label}
                    onClick={() => setDuration(d)}
                    data-testid={`guest-duration-${d.secs}`}
                    className={`flex-1 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${
                      duration.label === d.label ? "bg-revo-green text-navy" : "border border-white/15 text-revo-ice hover:border-revo-green"
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <button
            onClick={grant}
            disabled={granting || !!pass}
            data-testid="guest-grant-btn"
            className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-revo-green py-3.5 text-sm font-bold text-navy transition-transform duration-200 hover:scale-[1.02] disabled:opacity-50"
          >
            <UserPlus className="h-4 w-4" /> {granting ? "Issuing pass…" : pass ? "Pass active" : "Grant guest pass"}
          </button>
        </div>

        {/* Right: pass state */}
        <div className="relative flex min-h-[360px] flex-col items-center justify-center p-7 md:p-10">
          <AnimatePresence mode="wait">
            {granting ? (
              <motion.div key="issuing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-4">
                <div className="relative flex h-28 w-28 items-center justify-center">
                  {[0, 0.8].map((d) => (
                    <span key={d} className="absolute inset-0 rounded-full border border-revo-green/50" style={{ animation: `ring-expand 2s ${d}s ease-out infinite` }} />
                  ))}
                  <QrCode className="h-12 w-12 text-revo-green" />
                </div>
                <p className="text-sm text-revo-ice">Cloud signing entitlement token…</p>
              </motion.div>
            ) : pass ? (
              <motion.div key="active" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="w-full max-w-xs" data-testid="guest-active-pass">
                <div className="rounded-3xl border border-revo-green/40 bg-revo-green/5 p-6">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 rounded-full bg-revo-green/15 px-2.5 py-1 text-[10px] font-bold text-revo-green">
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-revo-green" /> ACTIVE PASS
                    </span>
                    <button onClick={revoke} data-testid="guest-revoke-btn" className="flex items-center gap-1 text-xs font-semibold text-revo-ice/60 transition-colors hover:text-white">
                      <X className="h-3.5 w-3.5" /> Revoke
                    </button>
                  </div>
                  <p className="mt-4 font-display text-2xl font-black text-white">{pass.name}</p>
                  <p className="text-xs text-revo-ice/60">Authorized on your surfaces</p>

                  <div className="mt-5 flex items-center gap-2 text-revo-ice">
                    <Clock className="h-4 w-4 text-revo-green" />
                    <span className="font-display text-lg font-bold text-white" data-testid="guest-countdown">{fmt(pass.remaining)}</span>
                    <span className="text-xs text-revo-ice/50">remaining</span>
                    <span className="ml-auto text-[10px] uppercase tracking-wider text-revo-ice/40">demo speed</span>
                  </div>
                  <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10">
                    <motion.div className="h-full rounded-full bg-revo-green" animate={{ width: `${(pass.remaining / pass.total) * 100}%` }} />
                  </div>

                  <div className="mt-5 flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                    <span className="text-sm text-revo-ice">Guest devices charging</span>
                    <span className="font-display text-xl font-extrabold text-revo-green" data-testid="guest-device-count">{pass.devices}</span>
                  </div>
                </div>
                <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-xs text-revo-ice/50">
                  <ShieldCheck className="h-3.5 w-3.5 text-revo-green" /> Everyone else on your account: unaffected.
                </p>
              </motion.div>
            ) : (
              <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-4 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03] text-revo-ice/40">
                  <ShieldCheck className="h-8 w-8" />
                </span>
                <p className="max-w-[220px] text-sm text-revo-ice/60">No active guest passes. Your power is yours alone.</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
